/**
 * Zod schemas for the JSON the twin loads: asset specs (strict, we author them)
 * and element files (loose, shipped by the previous dashboard with quirks such
 * as status strings that are really values).
 */
import { z } from '@/lib/schemaConfig';
import type { AssetSpec, Element } from '../types';
import { roofSurfaceSchema } from '../site/roofSurface';

const vec3 = z.tuple([z.number(), z.number(), z.number()]);

export const cameraPoseSchema = z.object({
  position: vec3,
  target: vec3,
  fov: z.number().positive().max(150).optional(),
});

const antennaSchema = z.object({
  kind: z.literal('antenna'),
  diameterM: z.number().positive(),
  rig: z.enum(['cassegrain', 'prime-focus', 'phased-array', 'optical']),
  pedestal: z.enum(['king-post', 'wheel-and-track', 'tripod', 'turret', 'tower']),
  radome: z.boolean().optional(),
  container: z.boolean().optional(),
  colour: z.string().optional(),
});

const satelliteSchema = z.object({
  kind: z.literal('satellite'),
  bus: z.enum(['box', 'cylinder', 'hexagonal']),
  lengthM: z.number().positive(),
  solarWings: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  dishes: z.number().int().nonnegative().optional(),
  colour: z.string().optional(),
});

const genericSchema = z.object({
  kind: z.enum(['building', 'vehicle', 'aircraft', 'ship', 'tower', 'cloud', 'person']),
  sizeM: z.number().positive(),
  colour: z.string().optional(),
});

const facilitySchema = z.object({
  kind: z.literal('facility'),
  monitoringMarker: z.literal('location-only').optional(),
  rfimsArrayReference: z.boolean().optional(),
  rfimsModelPlacement: z.object({ lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180), headingDeg: z.number().finite(), support: z.enum(['trailer', 'pads', 'roof']), roofBuildingId: z.number().int().positive().optional(), roofHeightM: z.number().positive().max(60).optional(), evidence: z.string() })
    .refine(p => p.support !== 'roof' || (p.roofBuildingId !== undefined && p.roofHeightM !== undefined), 'Rooftop equipment requires a building and fallback roof height').optional(),
  facilityId: z.string(),
  lat: z.number(),
  lon: z.number(),
  extentM: z.number().positive(),
  terminals: z.array(
    z.object({
      id: z.string(),
      assetId: z.string(),
      label: z.string(),
      lat: z.number(),
      lon: z.number(),
      diameterM: z.number().positive(),
      rig: antennaSchema.shape.rig,
      radome: z.boolean(),
      bands: z.array(z.string()),
    }),
  ),
  features: z.array(
    z.object({
      id: z.union([z.number(), z.string()]),
      kind: z.string(),
      tags: z.record(z.string(), z.string()),
      coordinates: z.array(z.tuple([z.number(), z.number()])),
      holes: z.array(z.array(z.tuple([z.number(), z.number()]))).optional(),
      display: z.boolean().optional(),
      roofSurface: roofSurfaceSchema.optional(),
      source: z
        .object({
          provider: z.string(),
          url: z.string(),
          license: z.string(),
          retrievedAt: z.string(),
          release: z.string().optional(),
          upstream: z.array(z.string()).optional(),
        })
        .optional(),
    }),
  ),
  mappingNote: z.string().optional(),
  interior: z
    .object({
      buildingId: z.union([z.string(), z.number()]),
      center: z.tuple([z.number(), z.number()]),
      rotation: z.number(),
      width: z.number().positive(),
      depth: z.number().positive(),
      sourceUrl: z.string().url(),
      sourceTitle: z.string(),
      rooms: z.array(
        z.object({
          id: z.string(),
          label: z.string(),
          purpose: z.string(),
          kind: z.enum(['racks', 'control', 'services']),
          center: z.tuple([z.number(), z.number()]),
          width: z.number().positive(),
          depth: z.number().positive(),
          equipment: z.array(z.object({ id: z.string(), label: z.string(), function: z.string() })),
        }),
      ),
    })
    .optional(),
});
export const proceduralSchema = z.discriminatedUnion('kind', [
  antennaSchema,
  satelliteSchema,
  genericSchema,
  facilitySchema,
]);

const bindingSchema = z.object({
  subsystemId: z.string().min(1),
  nodes: z.array(z.string().min(1)),
  label: z.string().optional(),
  parentId: z.string().optional(),
  pose: cameraPoseSchema.optional(),
  explode: z.object({ direction: vec3, distanceM: z.number().nonnegative() }).optional(),
});

const signalPathSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  direction: z.enum(['receive', 'transmit']),
  subsystemIds: z.array(z.string().min(1)).min(2),
  colour: z.string().optional(),
  visibleInSolid: z.boolean().optional(),
  routeNodes: z.array(z.string()).optional(),
});

const subviewSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  modelUrl: z.string().optional(),
  procedural: z.enum(['container-interior', 'lower-room']).optional(),
  pose: cameraPoseSchema,
  bindings: z.array(bindingSchema).optional(),
  signalPaths: z.array(signalPathSchema).optional(),
  interior: z.boolean().optional(),
  explodable: z.boolean().optional(),
  cameraBounds: z.object({ min: vec3, max: vec3 }).optional(),
});

export const assetSpecSchema = z
  .object({
    modelNote: z.string().optional(),
    space: z.boolean().optional(),
    globeLink: z
      .object({ network: z.string(), missionId: z.string().optional(), antennaId: z.string().optional() })
      .optional(),
    id: z.string().min(1),
    elementId: z.string().min(1),
    label: z.string(),
    modelUrl: z.string().optional(),
    procedural: proceduralSchema.optional(),
    scale: z.number().positive().optional(),
    poses: z
      .record(z.string(), cameraPoseSchema)
      .refine((p) => 'main' in p, { message: 'poses.main is required' }),
    bindings: z.array(bindingSchema),
    subviews: z.array(subviewSchema).optional(),
    signalPaths: z.array(signalPathSchema).optional(),
    site: z
      .object({
        antennaId: z.string().optional(),
        lat: z.number().min(-90).max(90),
        lon: z.number().min(-180).max(180),
        headingDeg: z.number().optional(),
        facilityId: z.string().optional(),
        location: z.string().optional(),
        sourceUrl: z.string().url().optional(),
        sourceTitle: z.string().optional(),
        mapUrl: z.string().optional(),
        description: z.string().optional(),
        extentM: z.number().positive().optional(),
      })
      .optional(),
    boundsM: vec3.optional(),
  })
  .refine((s) => Boolean(s.modelUrl || s.procedural), { message: 'an asset needs modelUrl or procedural' });

/** Throws with the zod issues joined when the spec is malformed. */
export function parseAssetSpec(json: unknown, source = 'asset spec'): AssetSpec {
  const r = assetSpecSchema.safeParse(json);
  if (!r.success) {
    const issues = r.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`).join('; ');
    throw new Error(`${source} is malformed: ${issues}`);
  }
  return r.data as AssetSpec;
}

const historyPointSchema = z.looseObject({
  datetime: z.string(),
  value: z.union([z.string(), z.number()]),
});

const dataItemSchema = z.looseObject({
  id: z.string(),
  label: z.string(),
  value: z.union([z.string(), z.number()]).transform((v) => String(v)),
  status: z.string().default('green'),
  description: z.string().optional(),
  history: z.array(historyPointSchema).optional(),
  normalRange: z.looseObject({ low: z.number(), high: z.number(), unit: z.string().optional() }).optional(),
});

interface SubsystemShape {
  id: string;
  label: string;
  data: z.infer<typeof dataItemSchema>[];
  childs?: SubsystemShape[];
  [key: string]: unknown;
}

const subsystemSchema: z.ZodType<SubsystemShape> = z.lazy(() =>
  z.looseObject({
    id: z.string(),
    label: z.string(),
    reference: z
      .object({
        sourceTitle: z.string(),
        basis: z.literal('illustrative').optional(),
        sourceUrl: z.url().refine((url) => url.startsWith('https://')),
        checkedAt: z.iso.date(),
        note: z.string().optional(),
        values: z.array(z.object({ label: z.string(), value: z.string() })).min(1),
      })
      .optional(),
    data: z.array(dataItemSchema).default([]),
    childs: z.array(subsystemSchema).optional(),
  }),
) as unknown as z.ZodType<SubsystemShape>;

export const elementSchema = z.looseObject({
  id: z.string(),
  subsystems: z.array(subsystemSchema),
});

export function parseElement(json: unknown, source = 'element'): Element {
  const r = elementSchema.safeParse(json);
  if (!r.success) {
    const issues = r.error.issues
      .slice(0, 5)
      .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('; ');
    throw new Error(`${source} is malformed: ${issues}`);
  }
  return r.data as unknown as Element;
}
